/***
 *
 *   PDF REPORT HELPERS
 *   Pure logic for the verification page "Download PDF" options. Kept separate
 *   from the view so the availability gating and media-usability rules can be
 *   unit tested without rendering the whole VerificationView.
 *
 **********/

import { getSanctionsRecordFields } from "./sanctions-record";

/**
 * Decide which optional PDF sections have data available for a verification —
 * the "when available" gating behind the download options panel.
 *
 * @param {object|null} verificationInfo - verification.data.verification[0]
 * @param {Array} imagesResponse - usable images already fetched for the page
 * @param {boolean} imagesUnavailable - true once media is known to be purged (>45 days)
 * @returns {{ adverseMediaResult: object|null, sanctionsAvailable: boolean,
 *   adverseMediaAvailable: boolean, imagesAvailable: boolean }}
 */
export function derivePdfAvailability(
  verificationInfo,
  imagesResponse,
  imagesUnavailable
) {
  const adverseMediaResult =
    verificationInfo?.adverse_media_results?.AML?.[0] || null;

  return {
    adverseMediaResult,
    // Screening ran when found_records is an array (even an empty one => "No matches").
    sanctionsAvailable: Array.isArray(
      verificationInfo?.sanctions_results?.found_records
    ),
    adverseMediaAvailable: !!adverseMediaResult,
    imagesAvailable: (imagesResponse?.length || 0) > 0 && !imagesUnavailable,
  };
}

/**
 * Format a single PEP/Sanctions screening record into ordered { label, value }
 * rows for the PDF. Delegates to the shared, defensive field builder so the PDF
 * shows the same expanded field set as the on-screen cards; the jsPDF packing
 * loop renders whatever rows come back.
 *
 * @param {object|undefined} record - one entry from sanctions_results.found_records
 * @returns {Array<{ label: string, value: string }>}
 */
export function formatSanctionsRecord(record) {
  return getSanctionsRecordFields(record);
}

/**
 * Decide whether a /api/veriff/media/:verificationId/:id response carried
 * usable image bytes.
 * Veriff purges verification media after ~45 days, after which the endpoint
 * returns an "unavailable" status (or the request fails outright).
 *
 * @param {object|undefined} responseData - the Axios response body (res.data)
 * @returns {boolean}
 */
export function isUsableMediaResponse(responseData) {
  return responseData?.status !== "unavailable" && !!responseData?.data?.image;
}
